import { BootstrapData } from '../api/types';
import { claimableChest, seasonFinaleState } from './game';
import { todayIso } from './format';
// createdAtLocalDay экспортируется из recording-streak
import { createdAtLocalDay } from './recording-streak';

// ---------------------------------------------------------------------------
// Типы
// ---------------------------------------------------------------------------

export interface InboxEvent {
  /** Стабильный ключ: 'chest_2026-07-27', 'finale_2026-07', 'milestone_Дача_50', 'partner_day_2026-07-28_<authorId>' */
  key: string;
  kind: 'chest' | 'finale' | 'milestone' | 'partner_day';
  title: string;
  sub?: string;
  /** Для сортировки: сундук — воскресенье недели, финал — 1-е, остальные — сегодня. */
  dateIso: string;
  tab: 'dashboard' | 'goals';
}

// ---------------------------------------------------------------------------
// localStorage (read-state локальный, v1 by design — §6 спеки)
// ---------------------------------------------------------------------------

export const INBOX_READ_KEY = 'inbox_read';

/** Загружает прочитанные ключи из localStorage. try/catch silent (конвенция repo). */
export function readSet(): Set<string> {
  try {
    const raw = localStorage.getItem(INBOX_READ_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return new Set(Object.keys(parsed));
    }
    return new Set();
  } catch {
    return new Set();
  }
}

/** Добавляет ключи в существующую map и сохраняет. try/catch silent. */
export function markAllRead(keys: string[]): void {
  try {
    const raw = localStorage.getItem(INBOX_READ_KEY);
    let map: Record<string, 1> = {};
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        map = parsed as Record<string, 1>;
      }
    }
    for (const key of keys) {
      map[key] = 1;
    }
    localStorage.setItem(INBOX_READ_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Пороги milestones для Входящих (§ брифа: только 50 и 100)
// ---------------------------------------------------------------------------

const INBOX_MILESTONE_THRESHOLDS = [50, 100] as const;

// ---------------------------------------------------------------------------
// Основная функция
// ---------------------------------------------------------------------------

/**
 * Строит список derived-событий Входящих на основе данных Bootstrap.
 * Функция чистая (кроме `now` для часов) — localStorage не трогает.
 */
export function buildInboxEvents(
  data: BootstrapData,
  myUserId: string | null,
  now: Date = new Date(),
): InboxEvent[] {
  const events: InboxEvent[] = [];
  const today = todayIso(now);

  // --- chest ---
  const chest = claimableChest(data, now);
  if (chest) {
    // Ключ стабилен по понедельнику; dateIso = воскресенье той же недели (пн + 6 дней).
    // Используем локальный конструктор Date, чтобы избежать TZ-сдвигов.
    const [cy, cm, cd] = chest.monday.split('-').map(Number);
    const sun = new Date(cy, cm - 1, cd + 6);
    const sunIso =
      `${sun.getFullYear()}-${String(sun.getMonth() + 1).padStart(2, '0')}-${String(sun.getDate()).padStart(2, '0')}`;
    events.push({
      key: `chest_${chest.monday}`,
      kind: 'chest',
      title: 'Сундук недели готов',
      sub: 'внутри уже что-то звенит',
      // dateIso = воскресенье недели сундука (для сортировки: конец той недели)
      dateIso: sunIso,
      tab: 'dashboard',
    });
  }

  // --- finale ---
  const finale = seasonFinaleState(data, now);
  if (finale && !finale.closed) {
    events.push({
      key: `finale_${finale.month}`,
      kind: 'finale',
      title: 'Финал сезона ждёт',
      sub: `итоги и лотерея за ${finale.month}`,
      // dateIso = 1-е число месяца финала
      dateIso: `${finale.month}-01`,
      tab: 'dashboard',
    });
  }

  // --- milestones ---
  for (const g of data.goals) {
    if (g.progress == null || !Number.isFinite(g.progress)) continue;
    const pct = g.progress * 100;
    for (const threshold of INBOX_MILESTONE_THRESHOLDS) {
      if (pct >= threshold) {
        const title =
          threshold === 50
            ? `Цель «${g.name}» — половина`
            : `Цель «${g.name}» собрана`;
        events.push({
          key: `milestone_${g.name}_${threshold}`,
          kind: 'milestone',
          title,
          dateIso: today,
          tab: 'goals',
        });
      }
    }
  }

  // --- partner_day ---
  // myUserId null (нет auth) → partner-событий нет (§ брифа)
  if (myUserId !== null) {
    // Собираем уникальных авторов среди операций ЗА СЕГОДНЯ (не-imp)
    const partnerAuthors = new Set<string>();
    for (const op of data.operations) {
      if (op.id.startsWith('imp_')) continue;
      if (!op.author_id) continue;
      if (op.author_id === myUserId) continue;
      // Проверяем, что операция записана сегодня (по created_at → локальный день)
      const localDay = createdAtLocalDay(op.created_at);
      if (localDay !== today) continue;
      partnerAuthors.add(op.author_id);
    }
    for (const authorId of partnerAuthors) {
      events.push({
        key: `partner_day_${today}_${authorId}`,
        kind: 'partner_day',
        title: 'Партнёр записал день',
        dateIso: today,
        tab: 'dashboard',
      });
    }
  }

  // --- Сортировка: dateIso desc, затем kind ---
  events.sort((a, b) => {
    if (b.dateIso !== a.dateIso) return b.dateIso.localeCompare(a.dateIso);
    return a.kind.localeCompare(b.kind);
  });

  return events;
}

// ---------------------------------------------------------------------------
// Счётчик непрочитанных
// ---------------------------------------------------------------------------

/** Количество событий, ключей которых нет в read. Чистая функция. */
export function unreadCount(events: InboxEvent[], read: Set<string>): number {
  return events.filter((e) => !read.has(e.key)).length;
}
